import { useCallback, useEffect, useRef, type RefObject } from 'react';

const FADE_DISTANCE_PX = 96;

export function useChatScrollFade(
  scrollRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  contentVersion: unknown,
): void {
  const rafRef = useRef<number | null>(null);

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    const panel = panelRef.current;
    if (!el || !panel) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const maxScroll = Math.max(0, scrollHeight - clientHeight);

    let topFade = 0;
    let bottomFade = 0;

    if (maxScroll > 1) {
      topFade = Math.min(1, scrollTop / FADE_DISTANCE_PX);
      bottomFade = Math.min(1, (maxScroll - scrollTop) / FADE_DISTANCE_PX);
    }

    panel.style.setProperty('--chat-fade-top', topFade.toFixed(3));
    panel.style.setProperty('--chat-fade-bottom', bottomFade.toFixed(3));
  }, [scrollRef, panelRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        updateFade();
        rafRef.current = null;
      });
    };

    updateFade();
    el.addEventListener('scroll', onScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => updateFade());
    resizeObserver.observe(el);

    const t = window.setTimeout(updateFade, 350);

    return () => {
      el.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
      window.clearTimeout(t);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [scrollRef, panelRef, updateFade, contentVersion]);
}
